import { capitalize, isString } from '#lib/utils/string.js';
import { nanoid } from 'nanoid';
import toast from 'react-hot-toast';

// format data
export function formatData(event) {
    try {
        const { type, value } = event.target;
        if (type === 'number') {
            return Number(value) || 0;
        }
        if (type === 'text') {
            return String(value);
        }
        return value;
    } catch (error) {
        console.error('Error formatting input:', error);
        return event.target.value;
    }
}
// custom function to create key value in select
export const defaultMakeReactKey = (option) => (isString(option) ? option : option?.id ?? option?.value);

// custom function to access value in select
export const defaultAccessValue = (option) => (isString(option) ? option : option?.value ?? option?.name);

// custom function to display value in select
export const defaultDisplayValue = (option) => (isString(option) ? option : option?.name ?? option?.value);

// function to set palettes for dropdowns
export function setOptions(palettes) {
    const newPalette = ['custom', ...Object.keys(palettes)];
    return newPalette;
}

export function translateLanguages(languages, language) {
    const languageNames = new Intl.DisplayNames([language ?? 'en'], { type: 'language' });
    return languages.map((lang) => {
        if (isString(lang.id)) lang.name = capitalize(languageNames.of(lang.id));
        return lang;
    });
}

// validate form post submit
export const validatePlanner = (errorCause, data, places, setError) => {
    let errors = [];

    // splitto le chiavi in arrivo e le salvo in un array di array che contengono le chiavi splittate
    const splittedKeys = Object.keys(errorCause.validationErrors).map((el) => el.split('.'));

    // parso ciascun errore
    splittedKeys.forEach((splittedArr) => {
        const err = {
            day: null, // sono id
            plan: null,
            place: null,
            field: null,
            room: null
        };
        if (splittedArr[0] === 'places') {
            err.place = places[splittedArr[1]].id;
            err.field = splittedArr[2];
            errors.push(err);
            return;
        }
        const keys = Object.keys(err);
        // inizializzo l'oggetto con la chiave giusta in base al component che genera l'errore
        let active = { [splittedArr[0]]: data };
        splittedArr.forEach((error, index) => {
            const foundKey = keys.find((key) => error.startsWith(key));
            if (foundKey) {
                active = active[error][splittedArr[index + 1]];
                err[foundKey] = active.id ?? active.index;
            }
        });
        err.field = splittedArr[splittedArr.length - 1];

        errors.push(err);
    });
    // rimuovo gli errori "fake", ovvero quelli che hanno come field l'indice del place dove fallisce la validazione
    // per farlo rimuovo tutti gli errori che hanno field valorizzato a numero intero
    const results = errors.filter((el) => isNaN(parseInt(el.field, 10)));
    setError(results);
};

export const compareObjectArrays = (array1, array2) => {
    if (!Array.isArray(array1) || !Array.isArray(array2)) {
        return false;
    }

    if (array1.length !== array2.length) {
        return false;
    }

    array1.sort();
    array2.sort();

    for (let i = 0; i < array1.length; i++) {
        const obj1 = array1[i];
        const obj2 = array2[i];

        // Confronta le proprietà di ogni oggetto
        for (const key in obj1) {
            if (obj1.hasOwnProperty(key) && obj2.hasOwnProperty(key) && obj1[key] !== obj2[key]) {
                return false;
            }
        }
    }

    return true;
};

export function getRandomInt(max) {
    return Math.floor(Math.random() * max);
}

export function getNRandomInt(max, n) {
    const randoms = [];
    for (let i = 0; i < n; i++) {
        let newRandom = getRandomInt(max);
        while (randoms.includes(newRandom)) newRandom = getRandomInt(max);
        randoms.push(newRandom);
    }
    return randoms;
}
export function isEqual(obj1, obj2) {
    var props1 = Object.getOwnPropertyNames(obj1);
    var props2 = Object.getOwnPropertyNames(obj2);
    if (props1.length != props2.length) {
        return false;
    }
    for (var i = 0; i < props1.length; i++) {
        let val1 = obj1[props1[i]];
        let val2 = obj2[props1[i]];
        let isObjects = isObject(val1) && isObject(val2);
        if ((isObjects && !isEqual(val1, val2)) || (!isObjects && val1 !== val2)) {
            return false;
        }
    }
    return true;
}
function isObject(object) {
    return object != null && typeof object === 'object';
}

export function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() is zero-based
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

export function formatReadableDateAndHours(string, lang) {
    if (!string) return _t.search.noPublication();
    const date = new Date(string);

    const readableDate = date.toLocaleDateString(lang ?? 'en', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    const readableTime = date.toLocaleTimeString(lang ?? 'en', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });

    return `${readableDate} ${readableTime}`;
}

export function generaPassword(lunghezza = 10) {
    const caratteriMinuscoli = 'abcdefghijklmnopqrstuvwxyz';
    const caratteriMaiuscoli = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numeri = '0123456789';
    const caratteriSpeciali = '@!?_-#$%^&*';

    // Controllo per assicurare una lunghezza minima di 10 caratteri
    if (lunghezza < 10) {
        lunghezza = 10;
    }

    // Genera password con crypto.getRandomValues: Math.random non e' adatto a materiale di sicurezza
    const randomIndex = (max) => crypto.getRandomValues(new Uint32Array(1))[0] % max;

    // Seleziona un carattere casuale da ciascun set di caratteri per rispettare i criteri minimi
    let password = '';
    password += caratteriMaiuscoli[randomIndex(caratteriMaiuscoli.length)];
    password += caratteriMinuscoli[randomIndex(caratteriMinuscoli.length)];
    password += numeri[randomIndex(numeri.length)];
    password += caratteriSpeciali[randomIndex(caratteriSpeciali.length)];

    // Riempi il resto della password con caratteri casuali da tutti i set
    const tuttiICaratteri = caratteriMinuscoli + caratteriMaiuscoli + numeri + caratteriSpeciali;
    for (let i = password.length; i < lunghezza; i++) {
        password += tuttiICaratteri[randomIndex(tuttiICaratteri.length)];
    }

    // Mescola i caratteri con Fisher-Yates per evitare una struttura prevedibile
    const chars = password.split('');
    for (let i = chars.length - 1; i > 0; i--) {
        const j = randomIndex(i + 1);
        [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    password = chars.join('');
    return password;
}

export const checkImageFormat = (file) => {
    const { type } = file;
    const extension = type.split('/')[1];
    if (extension === 'webp') {
        toast.error(_t.errors.invalidWebPImage());
        return false;
    }
    if (file.size > 1 * 1024 * 1024) {
        toast.error(_t.global.imageTooLarge());
        return false;
    }
    return true;
};

// NEW ID DEFINITION
export const newIdPrefix = 'NEW-';

export const newId = (id) => newIdPrefix + (id ?? nanoid());

// Function to decode html entities
function decodeHtmlEntities(text) {
    const htmlEntities = {
        '&egrave;': 'è',
        '&Egrave;': 'È',
        '&agrave;': 'à',
        '&Agrave;': 'À',
        '&eacute;': 'é',
        '&Eacute;': 'É',
        '&ugrave;': 'ù',
        '&Ugrave;': 'Ù',
        '&ograve;': 'ò',
        '&Ograve;': 'Ò',
        '&igrave;': 'ì',
        '&Igrave;': 'Ì',
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>',
        '&quot;': '"',
        '&apos;': "'",
        '&nbsp;': ' ',
        '&copy;': '©',
        '&reg;': '®',
        '&trade;': '™',
        '&euro;': '€',
        '&pound;': '£',
        '&yen;': '¥',
        '&cent;': '¢',
        '&sect;': '§',
        '&bull;': '•',
        '&hellip;': '…',
        '&ndash;': '–',
        '&mdash;': '—',
        '&lsquo;': '‘',
        '&rsquo;': '’',
        '&sbquo;': '‚',
        '&ldquo;': '“',
        '&rdquo;': '”',
        '&bdquo;': '„',
        '&laquo;': '«',
        '&raquo;': '»',
        '&lsaquo;': '‹',
        '&rsaquo;': '›',
        '&brvbar;': '¦',
        '&uml;': '¨',
        '&ordf;': 'ª',
        '&not;': '¬',
        '&shy;': '­',
        '&macr;': '¯',
        '&deg;': '°',
        '&plusmn;': '±',
        '&sup2;': '²',
        '&sup3;': '³',
        '&acute;': '´',
        '&micro;': 'µ',
        '&para;': '¶',
        '&middot;': '·',
        '&cedil;': '¸',
        '&sup1;': '¹',
        '&ordm;': 'º',
        '&frac14;': '¼',
        '&frac12;': '½',
        '&frac34;': '¾',
        '&iquest;': '¿',
        '&times;': '×',
        '&divide;': '÷'
    };

    return text.replace(/&[a-zA-Z]+;/g, (entity) => htmlEntities[entity] || entity);
}

function replaceHtmlTags(text) {
    return text.replace(/(<([^>]+)>)/gi, '');
}

export function convertsToPlainText(text) {
    if (!text) return '';
    if (typeof text !== 'string') return text;
    return replaceHtmlTags(decodeHtmlEntities(text));
}

export function createIntlTextForEditFields(key) {
    if (key === 'price') return _t.edit.fields[key](false);

    return _t.edit.fields[key]();
}
